import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ContactGroup, Person } from '../../../types';
import {
  exportContactsUserPortal,
  getContactGroupContactsUserPortal,
  getContactGroupsUserPortal,
  getContactsUserPortal,
  searchUserPortalContacts,
  UserKeys,
} from '../../api/users-api';
import { handleErrors } from '../../utils/errors';
import PersonalContactsLabels from '../user-management/user-details/personal-contacts/personal-contacts-labels';
import PersonalContactsGrid from '../user-management/user-details/personal-contacts/personal-contacts-grid';
import useDebounce from '../../utils/useDebounce';
import { useUserSettings } from '../../utils/use-user-settings';
import { Messages } from '../../utils/message-constants';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import SharedTabHeader from '../../shared/shared-tab-header';
export default function UserContactsPage() {
  const { userRoles, changeLogRoles, loggedInUser } = useUserSettings();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedContactGroup, setSelectedContactGroup] =
    useState<ContactGroup | null>(null);
  const [selectedContact, setSelectedContact] = useState<Person | null>(null);
  const [selectedLabel, setSelectedLabel] =
    useState<string>('contactGroups/all');
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: 100,
    page: 0,
  });

  const [searchText, setSearchText] = useState('');
  const debouncedInput = useDebounce(searchText, 200);

  const {
    data: searchedContacts,
    isLoading: isSearchedContactsLoading,
    isFetching: isSearchedContactsFetching,
  } = useQuery({
    queryKey: [UserKeys.ContactSearch, loggedInUser, debouncedInput],
    queryFn: () => searchUserPortalContacts(loggedInUser, debouncedInput),
    enabled: debouncedInput.length > 0,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contacts'),
    },
  });

  const {
    data: contactGroupsUserPortal,
    isLoading: labelsLoadingUserPortal,
    isFetching: labelsFetchingUserPortal,
  } = useQuery({
    queryKey: [UserKeys.Labels, loggedInUser],
    queryFn: () => getContactGroupsUserPortal(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contact labels'),
    },
  });

  const {
    data: groupMembersUserPortal,
    isLoading: groupMembersLoadingUserPortal,
    isFetching: groupMembersFetchingUserPortal,
  } = useQuery({
    queryKey: [UserKeys.ContactGroup, loggedInUser, selectedLabel],
    queryFn: () => getContactGroupContactsUserPortal(selectedLabel),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contact groups'),
    },
  });

  function getPageOfContacts() {
    const startIndex = paginationModel.page * paginationModel.pageSize;
    if (groupMembersUserPortal!.length > 100) {
      const memberSubset = groupMembersUserPortal!.slice(
        startIndex,
        paginationModel.pageSize * (paginationModel.page + 1)
      );
      return memberSubset;
    } else {
      return groupMembersUserPortal!;
    }
  }

  const isEnabled =
    groupMembersUserPortal !== undefined && groupMembersUserPortal.length > 0;

  const { data: personalContacts, isFetching: contactsFetchingUserPortal } =
    useQuery({
      queryKey: [
        UserKeys.Contacts,
        loggedInUser,
        selectedLabel,
        groupMembersUserPortal,
        paginationModel.page,
        paginationModel.pageSize,
      ],
      queryFn: () => getContactsUserPortal(getPageOfContacts()),
      enabled: isEnabled,
      meta: {
        errorMessage: Messages.Errors.Network.Loading('contacts'),
      },
    });

  const { mutate: userPortalMutate, isPending: isExportingUserPortal } =
    useMutation({
      mutationFn: () => exportContactsUserPortal('User'),
      onSuccess: (data) => {
        window.open(
          `https://docs.google.com/a/${
            loggedInUser.split('@')[1]
          }/spreadsheet/ccc?key=${data}#gid=0`,
          '_blank',
          'noopener, noreferrer'
        );
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Exporting('contacts'),
        });
      },
    });

  function handleMutate() {
    userPortalMutate();
  }

  return (
    <Box sx={{ p: 2, pt: 1 }}>
      <SharedTabHeader title="Personal Contacts" noDivider />

      <Stack
        flexDirection="row"
        gap={1}
        sx={{
          minHeight: 300,
          height: '62vh',
          my: 0,
          width: '95%',
          mx: 'auto',
        }}
        justifyContent="space-between"
      >
        <PersonalContactsLabels
          userEmail={loggedInUser}
          userView
          userRoles={userRoles}
          changeLogRoles={changeLogRoles}
          contactGroups={contactGroupsUserPortal}
          setSelectedRows={setSelectedRows}
          setSelectedContact={setSelectedContact}
          selectedContactGroup={selectedContactGroup}
          setSelectedContactGroup={setSelectedContactGroup}
          isLoadingLabels={labelsLoadingUserPortal}
          selectedLabel={selectedLabel}
          setSelectedLabel={setSelectedLabel}
        />

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <PersonalContactsGrid
            userView
            userRoles={userRoles}
            userEmail={loggedInUser}
            changeLogRoles={changeLogRoles}
            isLoading={
              contactsFetchingUserPortal ||
              groupMembersFetchingUserPortal ||
              labelsFetchingUserPortal ||
              labelsLoadingUserPortal ||
              groupMembersLoadingUserPortal ||
              isSearchedContactsLoading ||
              isSearchedContactsFetching
            }
            isExporting={isExportingUserPortal}
            personData={
              debouncedInput.length > 0 ? searchedContacts : personalContacts
            }
            contactGroups={contactGroupsUserPortal}
            groupMembers={groupMembersUserPortal}
            handleMutate={handleMutate}
            selectedLabel={selectedLabel}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            selectedContact={selectedContact}
            setSelectedContact={setSelectedContact}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            searchText={searchText}
            setSearchText={setSearchText}
          />
        </Box>
      </Stack>
    </Box>
  );
}
